import { gql, useQuery } from "@apollo/client";
import React from "react";
import { GetAuditEventsQuery } from "../gql/graphql";
import { ShareparkDateTime } from "./LocalTime";

interface AuditEventsProps {
  entityId: string; // GUID as a string
  entityType: string; // Entity name
}

const CHANGES = gql`
  query GetAuditEvents($entityId: String!, $entityType: String!) {
    auditEvents(
      where: {
        and: [
          { entityId: { eq: $entityId } }
          { entityType: { eq: $entityType } }
        ]
      }
      order: [{ eventDate: DESC }]
      skip: 0
      take: 1000
    ) {
      items {
        id
        eventDate
        eventType
        userName
        entityType
        entityId
        changes
      }
      totalCount
    }
  }
`;

const AuditEvents: React.FC<AuditEventsProps> = ({ entityId, entityType }) => {
  const { loading, error, data } = useQuery<GetAuditEventsQuery>(CHANGES, {
    variables: { entityId, entityType },
    fetchPolicy: "cache-and-network",
  });

  const displayChanges = (changes: any) => {
    if (changes) {
      const obj = JSON.parse(changes);

      if (obj.Action === "Update") {
        const valuesChanges = obj.Changes;

        const summary = valuesChanges.map((valueChange: any) => {
          let oldValue = valueChange.OriginalValue;
          let newValue = valueChange.NewValue;
          const fieldName = valueChange.ColumnName;

          if (typeof oldValue === "boolean") {
            oldValue = oldValue ? "Yes" : "No";
          }
          if (typeof newValue === "boolean") {
            newValue = newValue ? "Yes" : "No";
          }

          // Check if the values are integers and format them if necessary
          if (typeof oldValue === "number" && Number.isInteger(oldValue)) {
            oldValue = oldValue.toLocaleString(); // Adds commas for thousands
          }
          if (typeof newValue === "number" && Number.isInteger(newValue)) {
            newValue = newValue.toLocaleString(); // Adds commas for thousands
          }

          if (oldValue === null || oldValue === undefined || oldValue === "") {
            oldValue = "{empty}";
          }

          if (newValue === null || newValue === undefined || newValue === "") {
            newValue = "{empty}";
          }

          return (
            <div>
              <span className="text-muted">{fieldName}</span>:{" "}
              <span
                className="text-danger text-decoration-line-through"
                style={{
                  display: "inline-block",
                  maxWidth: "20ch", // Limit to 10 characters
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  verticalAlign: "middle",
                }}
              >
                {oldValue}
              </span>{" "}
              {"->"}{" "}
              <span
                className="text-success"
                style={{
                  display: "inline-block",
                  maxWidth: "20ch", // Limit to 10 characters
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  verticalAlign: "middle",
                }}
              >
                {newValue}
              </span>
            </div>
          );
        });

        return <pre>{summary}</pre>;
      }

      if (obj.Action === "Insert") {
        const colValues = obj.ColumnValues;

        const summary = Object.entries(colValues).map(([key, value]) => {
          let displayValue = value;
          if (typeof displayValue === "boolean") {
            displayValue = displayValue ? "Yes" : "No";
          }
          if (
            typeof displayValue === "number" &&
            Number.isInteger(displayValue)
          ) {
            displayValue = displayValue.toLocaleString(); // Adds commas for thousands
          }

          if (
            displayValue === null ||
            displayValue === undefined ||
            displayValue === ""
          ) {
            return null;
            // displayValue = "{empty}";
          }

          return (
            <div key={key}>
              <span className="text-muted">{key}</span>:{" "}
              <span className="text-success">{displayValue as string}</span>
            </div>
          );
        });

        return <pre>{summary}</pre>;
      }
    }
    return <></>;
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Changes</h1>
      </div>

      {loading && <p>Loading...</p>}

      {error && <p>Error: {error.message}</p>}

      {data &&
      data.auditEvents &&
      data.auditEvents.items &&
      data.auditEvents.items.length > 0 ? (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>When</th>
              <th>Type</th>
              <th>User</th>
              <th>Entity</th>
              <th>Changes</th>
            </tr>
          </thead>
          <tbody>
            {data.auditEvents.items.map((event) => (
              <tr key={event?.id}>
                <td>
                  <ShareparkDateTime
                    showAsInput={false}
                    timestamp={event?.eventDate}
                  />
                </td>
                <td>{event?.eventType}</td>
                <td>{event?.userName}</td>
                <td>{event?.entityType}</td>
                <td>{displayChanges(event?.changes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        !loading && <p>No changes found.</p>
      )}
    </>
  );
};

export default AuditEvents;
